import SanitizedText from 'sanitize-text/components/SanitizedText';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Image } from 'foundations-components/transitional/components/Image';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Timestamp } from './Timestamp';
import I18n from 'I18n';
import { UserIcon } from 'foundations-assets/icons/components/UserIcon';
import { getInitials } from '../utils/getInitials';
import { useMessageAttachments } from '../hooks/useMessageAttachments';
import { MessageAttachment } from './MessageAttachment';
import { MessageImage } from './MessageImage';
import { SensitiveHiddenMessage } from './SensitiveHiddenMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SENSITIVE = 'SENSITIVE';
const ThreadMessageAttachments = ({
  images,
  attachments
}) => {
  if (images.length === 0 && attachments.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [images.length > 0 && /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      gap: "200",
      alignItems: "start",
      maxInlineSize: "100%",
      children: images.map(image => /*#__PURE__*/_jsx(MessageImage, {
        image: image
      }, image.fileId))
    }), attachments.length > 0 && /*#__PURE__*/_jsx(Flex, {
      flexDirection: "row",
      gap: "200",
      flexWrap: "wrap",
      children: attachments.map(attachment => /*#__PURE__*/_jsx(MessageAttachment, {
        attachment: attachment.attachment,
        sensitiveAttachment: attachment.sensitiveAttachment,
        inThread: true
      }, attachment.fileId))
    })]
  });
};
const ThreadMessageBody = ({
  isSensitive,
  isHidden,
  messageRichText,
  messageText,
  images,
  attachments
}) => {
  const hasContent = messageRichText || messageText;
  const noPlainText = (messageText === null || messageText === void 0 ? void 0 : messageText.trim()) === '';
  if (isSensitive) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(SensitiveHiddenMessage, {}), /*#__PURE__*/_jsx(ThreadMessageAttachments, {
        images: images,
        attachments: attachments
      })]
    });
  }
  if (isHidden) {
    return /*#__PURE__*/_jsx(Text, {
      fontStyle: "italic",
      typography: "body-100",
      color: "text-core-subtle",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customer-portal-app.ticketThread.messageContent.isHidden"
      })
    });
  }
  if (!hasContent) {
    return /*#__PURE__*/_jsx(Text, {
      fontStyle: "italic",
      typography: "body-100",
      color: "text-core-subtle",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customer-portal-app.ticketThread.messageContent.noContent"
      })
    });
  }
  if (noPlainText) {
    return /*#__PURE__*/_jsx(ThreadMessageAttachments, {
      images: images,
      attachments: attachments
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Text, {
      as: "div",
      typography: "body-100",
      color: "text-core-default",
      children: /*#__PURE__*/_jsx(SanitizedText, {
        body: (messageRichText === null || messageRichText === void 0 ? void 0 : messageRichText.replace(/(background-)?color\s*:[^;"']+/gi, '')) || messageText || I18n.text('customer-portal-app.ticketThread.messageContent.noContent'),
        formattingOptions: {
          shouldPreventAutoLinker: true
        }
      })
    }), /*#__PURE__*/_jsx(ThreadMessageAttachments, {
      images: images,
      attachments: attachments
    })]
  });
};
export const ThreadMessage = ({
  message,
  responders,
  attachmentsMap,
  sensitiveAttachmentsMetadata
}) => {
  const isInitialMessage = message['@type'] === 'INITIAL_MESSAGE';
  const messageSender = 'sender' in message ? message.sender : undefined;
  const messageHiddenBy = 'hiddenBy' in message ? message.hiddenBy : undefined;
  const messageSensitivityType = 'sensitivityType' in message ? message.sensitivityType : undefined;
  const messageRichText = 'richText' in message ? message.richText : undefined;
  const messageText = 'text' in message ? message.text : undefined;
  const responderKey = messageSender ? JSON.stringify(messageSender) : undefined;
  const threadResponder = responderKey ? responders[responderKey] : undefined;
  const isHidden = Boolean(messageHiddenBy);
  const isSensitive = messageSensitivityType === SENSITIVE;
  const avatarUrl = threadResponder === null || threadResponder === void 0 ? void 0 : threadResponder.avatar;
  const avatarInitials = getInitials(threadResponder === null || threadResponder === void 0 ? void 0 : threadResponder.displayName);
  const [images, attachments] = useMessageAttachments([message], attachmentsMap, sensitiveAttachmentsMetadata);
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "row",
    gap: "600",
    alignItems: "start",
    children: [/*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      blockSize: "fit-content",
      children: avatarUrl ? /*#__PURE__*/_jsx(Image, {
        src: avatarUrl,
        alt: (threadResponder === null || threadResponder === void 0 ? void 0 : threadResponder.displayName) || I18n.text('customer-portal-app.ticketThread.messageContent.avatarAltText'),
        shape: "circle",
        responsive: false,
        inlineSize: "40px",
        blockSize: "40px"
      }) : /*#__PURE__*/_jsx(Flex, {
        borderRadius: "full",
        backgroundColor: "fill-primary-default",
        inlineSize: "40px",
        blockSize: "40px",
        alignItems: "center",
        justifyContent: "center",
        children: avatarInitials ? /*#__PURE__*/_jsx(Text, {
          typography: "heading-25",
          color: "text-primary-default",
          children: avatarInitials
        }) : /*#__PURE__*/_jsx(UserIcon, {
          size: "500",
          color: "icon-primary-default"
        })
      })
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "200",
      flex: "1",
      minInlineSize: "0",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "row",
        gap: "200",
        alignItems: "baseline",
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "heading-100",
          children: isInitialMessage ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customer-portal-app.ticketThread.messageContent.welcomeMessageTypeHeader"
          }) : threadResponder ? threadResponder.displayName : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customer-portal-app.ticketThread.messageContent.automatedMessageTypeHeader"
          })
        }), /*#__PURE__*/_jsx(Text, {
          typography: "detail-100",
          color: "text-core-default",
          "data-test-id": "thread-message-timestamp",
          children: /*#__PURE__*/_jsx(Timestamp, {
            timestamp: message.timestamp
          })
        })]
      }), /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        gap: "200",
        blockSize: "100%",
        minInlineSize: "0",
        children: /*#__PURE__*/_jsx(ThreadMessageBody, {
          isSensitive: isSensitive,
          isHidden: isHidden,
          messageRichText: messageRichText,
          messageText: messageText,
          images: images,
          attachments: attachments
        })
      })]
    })]
  });
};